/**
 * Prezentacja pól KSeF wspólna dla listy sprzedaży i formatki dokumentu.
 *
 * SŁOWNIK STATUSÓW POCHODZI OD UŻYTKOWNIKA, NIE Z DOKUMENTACJI. Symfonia zwraca status KSeF jako
 * liczbę i nigdzie nie publikuje jej znaczenia — ani swagger, ani opis WebAPI. Kody poniżej
 * przekazał właściciel systemu (2026-09-11) i to jedyne ich źródło. Wniosek na przyszłość: jeśli
 * pojawi się kod spoza tej listy, UI pokazuje SUROWĄ LICZBĘ zamiast zgadywać — lepiej pokazać
 * „45" niż podpisać dokument podatkowy etykietą wziętą z sufitu.
 *
 * Ścieżka dokumentu w KSeF jest dwuetapowa i to widać w kodach: najpierw trafia do Symfonia KSeF
 * (10–30), potem stamtąd do KSeF (40–70). Dlatego „błąd wysyłki" występuje w dwóch wariantach,
 * a kolor rozróżnia trzy stany: sukces, droga w toku, problem wymagający reakcji.
 */

import { useTranslation } from 'react-i18next';
import type { HmInvoiceKind } from '@saas/shared';

/** Wydźwięk statusu — steruje kolorem i tym, czy użytkownik ma się czymś zająć. */
type KsefTone = 'neutral' | 'progress' | 'success' | 'error';

/**
 * Kody statusu KSeF wg słownika Symfonii. Klucz i18n leży w `tenant.handelMagazyn.ksef.status.*`;
 * kod spoza tej mapy renderujemy jako liczbę (patrz nagłówek).
 */
export const KSEF_STATUS_TONES: Record<number, KsefTone> = {
  0: 'neutral', // pusty
  10: 'neutral', // oczekuje na wysyłkę do Symfonia KSeF
  20: 'error', // błąd wysyłki do Symfonia KSeF
  30: 'progress', // przesłano do Symfonia KSeF
  40: 'error', // błąd wysyłki z Symfonia KSeF do KSeF
  50: 'progress', // wysłano do KSeF, trwa walidacja
  60: 'success', // faktura zatwierdzona w KSeF
  70: 'error', // faktura odrzucona przez KSeF
  80: 'neutral', // wyeksportowano do pliku KSeF
  90: 'neutral', // zaimportowano z pliku KSeF
};

const TONE_CLASS: Record<KsefTone, string> = {
  neutral: 'bg-bg-tertiary text-text-secondary',
  progress: 'bg-sky-100 text-sky-800',
  success: 'bg-emerald-100 text-emerald-800',
  error: 'bg-red-100 text-red-800',
};

/** Klucz i18n etykiety trybu KSeF; nieznany tryb ma własny klucz, nie kreskę. */
export function invoiceKindKey(kind: HmInvoiceKind): string {
  return `tenant.handelMagazyn.ksef.kind.${kind}`;
}

export function InvoiceKindBadge({ kind }: { kind: HmInvoiceKind }) {
  const { t } = useTranslation();
  const label = t(invoiceKindKey(kind), { defaultValue: kind });
  // Dokument poza KSeF to stan normalny (papier), nie brak danych — stąd neutralny, nie alarmowy.
  const structured = kind === 'Structured' || kind === 'EInvoice';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
        structured ? 'bg-sky-100 text-sky-800' : 'bg-bg-tertiary text-text-secondary'
      }`}
    >
      {label}
    </span>
  );
}

/**
 * Status KSeF. Brak kodu przy dokumencie papierowym znaczy „nie dotyczy"; przy ustrukturyzowanym
 * — że Symfonia statusu nie podała.
 */
export function KsefStatus({ code, kind }: { code: number | null; kind: HmInvoiceKind }) {
  const { t } = useTranslation();

  if (code == null) {
    // Dokument papierowy NIE MA statusu KSeF i to jest pełna odpowiedź, a nie brak danych —
    // stąd „nie dotyczy" zamiast kreski. Kreska zostaje dla dokumentu w KSeF bez statusu.
    const outsideKsef = kind === 'ForcedPaper' || kind === 'NoInvoice';
    return (
      <span className="text-text-tertiary" title={t('tenant.handelMagazyn.ksef.statusHint')}>
        {outsideKsef ? t('tenant.handelMagazyn.ksef.notApplicable') : '—'}
      </span>
    );
  }

  const tone = KSEF_STATUS_TONES[code];
  // Kod spoza słownika: pokazujemy liczbę i mówimy wprost, że to nieznany kod Symfonii.
  if (!tone) {
    return (
      <span
        className="font-mono text-xs text-text-primary"
        title={t('tenant.handelMagazyn.ksef.statusCodeHint')}
      >
        {code}
      </span>
    );
  }

  const label = t(`tenant.handelMagazyn.ksef.status.${code}`, { defaultValue: String(code) });
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}
      title={`${label} (${code})`}
    >
      {label}
    </span>
  );
}
